import React, { useEffect, useRef, useState } from 'react';
import './globals.css'; // Tailwind v4
import 'react-toastify/dist/ReactToastify.css';
import accessLocalStorage, { ls } from 'services/localStorage';
import { TRACK } from 'services/analytics';
import Tracking from './tracking';
import AppRoutes from './routes';
import Spinner from './components/spinners/global';
import useAuth from 'state/auth/hooks/useAuth';
import useStore from 'state/store/hooks/useStore';
import APIErrorHandler from 'services/api/errorHandler';
import { BrowserRouter } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import useQuery from 'utils/hooks/useQuery';
import { isDashboardInNativeMode } from 'utils/dashboard';
import { refreshSession, canSpendRefreshToken, describeError } from 'utils/api';
import type { ISSOTokens } from 'utils/api';
import { SHELL_ORIGIN } from 'utils/config';
import { REFRESH_TOKENS_MESSAGE } from 'utils/shell-messages';
import {
  isTokenLapsed,
  readTokenSubject,
  readTokenExpiry,
} from 'services/auth';
import redirectToLogin, {
  claimSilentLoginAttempt,
  resetSilentLogin,
} from 'utils/redirectToLogin';
import routeConfig from 'routes/config';
import NativeRouteSync from 'utils/hooks/useNativeRouteSync';

const PARENT_HANDOFF_TIMEOUT_MS = 5000;

const DEFAULT_SESSION_TTL_SECONDS = 3600;

export const redeemTokens = async (
  accessToken: string,
  refreshToken?: string
): Promise<ISSOTokens | null> => {
  if (!isTokenLapsed(accessToken)) {
    return refreshToken ? { accessToken, refreshToken } : null;
  }

  if (!refreshToken) return null;

  if (!canSpendRefreshToken()) return null;

  try {
    const renewed = await refreshSession(accessToken, refreshToken);
    return renewed;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Token refresh failed during bootstrap:', describeError(e));
    return null;
  }
};

export const storedSessionBeatsHandoff = (
  stored: { accessToken?: string; refreshToken?: string } | null,
  handoffAccessToken: string,
  handoffRefreshToken?: string
): boolean => {
  if (!stored?.accessToken || !stored?.refreshToken) return false;

  const storedSubject = readTokenSubject(stored.accessToken);
  const handoffSubject = readTokenSubject(handoffAccessToken);
  if (!storedSubject || storedSubject !== handoffSubject) return false;

  if (!isTokenLapsed(stored.accessToken) && isTokenLapsed(handoffAccessToken)) {
    return true;
  }

  const storedExpiry = readTokenExpiry(stored.refreshToken);
  const handoffExpiry = handoffRefreshToken
    ? readTokenExpiry(handoffRefreshToken)
    : null;

  if (storedExpiry === null || handoffExpiry === null) return false;
  return storedExpiry > handoffExpiry;
};

export const hasDeepLinkIntent = (
  pathname: string,
  search: URLSearchParams
): boolean => {
  if (search.get('src') === 'mso') return true;
  if (search.get(ls.REDIRECT_ORG_ID)) return true;

  if (pathname.startsWith(routeConfig.AUTH.layout)) return false;
  return pathname !== routeConfig.ROOT;
};

function App() {
  const [isLoaded, setIsLoaded] = useState(false);
  const { fetchTenant, auth, toLoginSSO, toSetNativeMode, toLoginWithTokens } = useAuth();
  const { loggedIn } = auth;
  const { toFetchStoresList } = useStore();
  const urlQuery = useQuery(location.search);

  const sessionInit = useRef<{
    accessToken: string | null;
    loaded: Promise<void>;
  } | null>(null);

  const loadSession = async (accessToken: string | null) => {
    if (!accessToken) return;
    await fetchTenant();
    await toFetchStoresList();
  };

  const startSession = (accessToken: string | null) =>
    loadSession(accessToken).catch((e) => {
      console.error('App init failed:', e);
      if (sessionInit.current?.accessToken === accessToken) {
        sessionInit.current = null;
      }
    });

  const appInit = async () => {
    const accessToken = accessLocalStorage.getUser()?.accessToken ?? null;

    const existing = sessionInit.current;
    const session =
      existing && existing.accessToken === accessToken
        ? existing
        : { accessToken, loaded: startSession(accessToken) };
    sessionInit.current = session;

    await session.loaded;
    // never leave the app stuck behind the global spinner
    setIsLoaded(true);
  };

  useEffect(() => {
    if (loggedIn) {
      appInit();
    }
  }, [loggedIn]);

  const bootstrap = async () => {
    const embedAccessToken = urlQuery.get('accessToken');
    const embedRefreshToken = urlQuery.get('refreshToken');

    const redirectOrgId = urlQuery.get(ls.REDIRECT_ORG_ID);
    const treez_code = urlQuery.get('code');

    const hasHandoffTokens = Boolean(embedAccessToken);
    const embedded = isDashboardInNativeMode();

    let handoff: ISSOTokens | null = null;

    if (hasHandoffTokens) {
      const stored = accessLocalStorage.getUser();
      const keepStored = storedSessionBeatsHandoff(
        stored,
        embedAccessToken as string,
        embedRefreshToken ?? undefined
      );

      let user: { accessToken: string; refreshToken?: string };

      if (keepStored) {
        user = {
          accessToken: stored.accessToken,
          refreshToken: stored.refreshToken,
        };
      } else {
        handoff = await redeemTokens(
          embedAccessToken as string,
          embedRefreshToken ?? undefined
        );

        user = handoff ?? {
          accessToken: embedAccessToken as string,
          refreshToken: embedRefreshToken ?? undefined,
        };

        if (user.refreshToken) {
          accessLocalStorage.setUser(user);
        } else {
          accessLocalStorage.updateUser(user);
        }
      }

      if (embedded) toSetNativeMode(true);
      toLoginWithTokens({ expiresIn: DEFAULT_SESSION_TTL_SECONDS, ...user });

      try {
        const cleaned = new URL(window.location.href);
        cleaned.searchParams.delete('accessToken');
        cleaned.searchParams.delete('refreshToken');
        window.history.replaceState({}, '', cleaned.toString());
      } catch (e) {}
    }

    const nativeMode = hasHandoffTokens && embedded;

    TRACK.initialize(nativeMode);

    if (redirectOrgId) {
      accessLocalStorage.setRedirectOrgId(redirectOrgId);
    }

    if (treez_code) {
      await toLoginSSO(treez_code);
      if (accessLocalStorage.getUser()?.accessToken) {
        resetSilentLogin();
      }
      appInit();
      return;
    }

    const stored = accessLocalStorage.getUser();
    if (stored?.accessToken && !hasHandoffTokens) {
      const renewed = await redeemTokens(
        stored.accessToken,
        stored.refreshToken
      );
      if (renewed) accessLocalStorage.updateUser(renewed);
    }

    const current = accessLocalStorage.getUser();
    if (
      isDashboardInNativeMode() &&
      current?.accessToken &&
      isTokenLapsed(current.accessToken)
    ) {
      window.parent.postMessage(
        { type: REFRESH_TOKENS_MESSAGE },
        SHELL_ORIGIN || '*'
      );
      setTimeout(() => setIsLoaded(true), PARENT_HANDOFF_TIMEOUT_MS);
      return;
    }

    const isAuthenticated = Boolean(accessLocalStorage.getUser()?.accessToken);

    if (
      !isAuthenticated &&
      !nativeMode &&
      hasDeepLinkIntent(window.location.pathname, urlQuery)
    ) {
      if (isDashboardInNativeMode()) {
        window.parent.postMessage(
          { type: REFRESH_TOKENS_MESSAGE },
          SHELL_ORIGIN || '*'
        );
        setTimeout(() => setIsLoaded(true), PARENT_HANDOFF_TIMEOUT_MS);
        return;
      }

      if (claimSilentLoginAttempt()) {
        redirectToLogin();
        return;
      }
    }

    appInit();
  };

  useEffect(() => {
    bootstrap().catch((e) => {
      // eslint-disable-next-line no-console
      console.error('bootstrap failed:', describeError(e));
      setIsLoaded(true);
    });
  }, []);

  return (
    <BrowserRouter>
      <Tracking />
      <NativeRouteSync />
      <Spinner isLoading={isLoaded ? 0 : 1} />
      <APIErrorHandler />
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      {isLoaded ? <AppRoutes /> : <></>}
    </BrowserRouter>
  );
}

export default App;
